'use client';

import React, { useEffect, useState } from 'react';

import IMAGE_SIZE from '../../constants/image-size.constants';
import { OPERATORS } from '../../constants/constants';
import { useOptions, useOptionsDispatch } from '@/providers/options';
import LoadMoreButton from '@/components/Buttons/LoadMoreButton';
import { paginateOnDemandList } from '@/utils/ondemand/client';
import OnDemandSummaryCard from '@/components/OnDemand/SummaryCard';
import { EMPTY_EVENT_FILTER } from '@/constants/article.constants';
import EventFilterDrawer from '@/components/Select/EventFilter';
import { Badge, CircularProgress, IconButton } from '@mui/material';
import { FilterAltSharp } from '@mui/icons-material';
import EventFilterBar from '@/components/Select/EventFilterBar';
import type { TypeOnDemandWithoutUnresolvableLinksResponse } from '@/contentful/types';

type OnDemandListProps = {
  onDemandItems: TypeOnDemandWithoutUnresolvableLinksResponse[];
  pageTitle: string;
  total: number;
  showPrices?: boolean;
  eventTypes: ContentPillar[];
  categories: ContentPillar[];
  sectors: ContentPillar[];
  locations: ContentPillar[];
};

const PAGE_SIZE = 12;
let initial = true;

const OnDemandListView = ({
  pageTitle,
  onDemandItems: initialList,
  total: initialTotal,
  showPrices,
  eventTypes,
  categories,
  sectors,
  locations
}: OnDemandListProps) => {
  const options = useOptions();
  const setOptions = useOptionsDispatch();

  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [onDemandList, setOnDemandList] =
    useState<TypeOnDemandWithoutUnresolvableLinksResponse[]>(initialList);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [filter, setFilter] = useState<EventFilter>(EMPTY_EVENT_FILTER);
  const [foundAll, setFoundAll] = useState<boolean>(
    initialList.length < PAGE_SIZE || initialList.length === initialTotal
  );
  // const categoryOperator = OPERATORS.AND;
  const [mobileFilterOpen, setMobileFilterOpen] = useState<boolean>(false);
  const [categoryOperator, setCategoryOperator] = useState<string>(
    OPERATORS.AND
  );

  const handleFilterChange = ({
    newFilter,
    operator
  }: {
    newFilter: EventFilter;
    operator: string;
  }) => {
    setFilter(newFilter);
    setCategoryOperator(operator);
    setCurrentPage(0);
    setFoundAll(false);
  };

  const loadMoreContent = async () => {
    setLoading(true);
    setLoadingMore(true);
    if (!foundAll) {
      const query: FilterQuery = {
        // start: currentPage * PAGE_SIZE,
        start: currentPage * PAGE_SIZE,
        rows: PAGE_SIZE,
        categoryOperator
      };

      if (filter.sector && filter.sector.length > 0) {
        query.sector = filter.sector.join(',');
      }

      if (filter.locationFilter && filter.locationFilter.length > 0) {
        query.locationFilter = filter.locationFilter.join(',');
      }

      if (filter.category && filter.category.length > 0) {
        query.category = filter.category.join(',');
      }

      if (filter.eventType && filter.eventType.length > 0) {
        query.eventType = filter.eventType.join(',');
      }

      try {
        const { onDemandList: newItems, total: newTotal } =
          await paginateOnDemandList(query);
        // setFoundAll(false);
        if (
          newItems.length < PAGE_SIZE ||
          onDemandList.length + newItems.length >= newTotal
        ) {
          setFoundAll(true);
        }
        setOnDemandList([...onDemandList, ...newItems]);
        setCurrentPage(currentPage + 1);
      } catch (err) {
        console.log(err);
      }

      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    const getOnDemandList = async (
      newFilter?: EventFilter,
      operator?: string
    ) => {
      const filterToFetch = newFilter ?? filter;
      const matchOperator = operator ?? categoryOperator;
      setLoading(true);
      window && window.scrollTo(0, 0);
      setCurrentPage(1);

      const query: FilterQuery = {
        start: 0,
        rows: PAGE_SIZE,
        categoryOperator: matchOperator
      };

      if (filterToFetch.sector && filterToFetch.sector.length > 0) {
        query.sector = filterToFetch.sector.join(',');
      }

      if (
        filterToFetch.locationFilter &&
        filterToFetch.locationFilter.length > 0
      ) {
        query.locationFilter = filterToFetch.locationFilter.join(',');
      }

      if (filterToFetch.category && filterToFetch.category.length > 0) {
        query.category = filterToFetch.category.join(',');
      }

      if (filterToFetch.eventType && filterToFetch.eventType.length > 0) {
        query.eventType = filterToFetch.eventType.join(',');
      }

      try {
        const { onDemandList: response, total: updatedTotal } =
          await paginateOnDemandList(query);
        setFoundAll(false);
        if (
          response.length < 1 ||
          response.length < PAGE_SIZE ||
          response.length >= updatedTotal
        ) {
          setFoundAll(true);
        }
        setOnDemandList(response);
      } catch (err) {
        console.log(err);
      }
      setLoading(false);
    };

    if (!initial) {
      void getOnDemandList();
    } else {
      initial = false;
    }
  }, [filter, categoryOperator]);

  const filtered =
    filter.category.length > 0 ||
    filter.locationFilter.length > 0 ||
    filter.sector.length > 0 ||
    filter.eventType.length > 0;

  return (
    <div className='theme-container ondemand-container'>
      {/* <EventFilterDrawer
        open={mobileFilterOpen}
        filter={filter}
        handleFilterChange={handleFilterChange}
        clearFilters={() => {
          setFilter(EMPTY_EVENT_FILTER);
          setMobileFilterOpen(false);
        }}
        closeFilter={() => setMobileFilterOpen(false)}
        eventTypes={eventTypes}
        sectors={sectors}
        locations={locations}
        categories={categories}
      /> */}
      <div className='standard-page-main'>
        <div className='standard-page__header ondemand-header'>
          <div className='standard-page__title'>
            <h1
              style={{
                fontSize: '1em',
                lineHeight: '1.8',
                textTransform: 'uppercase',
                letterSpacing: '1.5px'
              }}
            >
              {pageTitle}
            </h1>
            <div className='standard-page__title-underline'>{}</div>
          </div>
          {/* <div className='filter-mobile'>
            <Badge
              color='primary'
              overlap='circular'
              badgeContent={
                filtered
                  ? filter.category.length +
                    filter.locationFilter.length +
                    filter.sector.length +
                    filter.eventType.length
                  : 0
              }
            >
              <IconButton
                aria-label='Close'
                onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
              >
                <FilterAltSharp sx={{ color: '#1c1c1c' }} />
              </IconButton>
            </Badge>
          </div> */}
        </div>
        {/* <div className='standard-page__filter-bar'>
          <EventFilterBar
            filter={filter}
            handleFilterChange={handleFilterChange}
            clearFilters={() => {
              setFilter(EMPTY_EVENT_FILTER);
            }}
            eventTypes={eventTypes}
            sectors={sectors}
            locations={locations}
            categories={categories}
          />
        </div> */}
        <div className='ondemand-list'>
          {loading && !loadingMore ? (
            <div className='loading__inner'>
              <CircularProgress size={60} thickness={5} />
            </div>
          ) : (
            onDemandList.map((item, idx) => (
              <OnDemandSummaryCard
                key={item.sys.id}
                content={item}
                imageSize={
                  !(idx % 4)
                    ? IMAGE_SIZE.ON_DEMAND__THUMBNAIL__LARGE
                    : IMAGE_SIZE.ON_DEMAND__THUMBNAIL__SMALL
                }
                className={'item'}
                showPrice={showPrices}
              />
            ))
          )}
        </div>
        {onDemandList.length === 0 && !loading && (
          <div className='empty-result'>
            <h2>No On Demand content found</h2>
          </div>
        )}
        {loading && !loadingMore ? null : !foundAll &&
          onDemandList.length > 0 ? (
          <LoadMoreButton
            onClick={() => loadMoreContent()}
            hasMore={!foundAll}
            loading={loadingMore}
          />
        ) : null}
      </div>
    </div>
  );
};

export default OnDemandListView;
